import { cx, css } from '@mildang/styled-system/css';
import { splitCssProps } from '@mildang/styled-system/jsx';
import {
  type TypographyVariantProps,
  typography,
  type TypographyVariant,
} from '@mildang/styled-system/recipes';
import type {
  JsxStyleProps,
  SystemStyleObject,
  UnstyledProps,
} from '@mildang/styled-system/types';
import type { PandaStylePropName } from '../../types/HTMLStyledProps';
import {
  type ComponentPropsWithRef,
  type ElementType,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  type Ref,
  type RefAttributes,
} from 'react';

type FixedForwardRef = <T, P = {}>(
  render: (props: P, ref: Ref<T>) => ReactNode,
) => (props: P & RefAttributes<T>) => ReactNode;

const fixedForwardRef = forwardRef as FixedForwardRef;

type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any ? Omit<T, TOmitted> : never;

/**
 * DOM props 는 `ComponentPropsWithRef` 항 **하나로만** 받는다. `as` 를 안 넘긴 `<Text>` 는 `p` 로
 * 폴백한다.
 *
 * React 쪽에서 panda style prop 과 이름이 같은 속성은 전부 지운다 — `splitCssProps` 가 그 이름을
 * 무조건 style 로 가르므로 DOM 에 닿지 않는다(사유는 `PandaStylePropName` 주석).
 *
 * ⚠️ **panda 쪽 항으로 `PandaHTMLStyledProps<TAs>` 를 쓰지 않는다** — 제네릭 `TAs` 는 `ElementType`
 * 거대 union 으로 인스턴스화되고, 그 별칭의 비분배 `Omit` 이 union 을 `{}` 로 붕괴시킨다(실측).
 * 분배형으로 바꾸면 `Assign` 에서 TS2590. 그래서 여기서는 panda 항만 직접 교차한다.
 */
export type TypographyProps<TAs extends ElementType> = TypographyVariantProps &
  DistributiveOmit<
    ComponentPropsWithRef<ElementType extends TAs ? 'p' : TAs>,
    'as' | PandaStylePropName
  > & {
    as?: TAs;
  } & UnstyledProps &
  Omit<JsxStyleProps, 'css'> & {
    sx?: SystemStyleObject | SystemStyleObject[];
  };

function TextBase<TAs extends ElementType>(props: TypographyProps<TAs>, ref: ForwardedRef<any>) {
  const [cssProps, restProps] = splitCssProps(props);
  const { css: _cssProp, ...styleProps } = cssProps;
  const { sx, ref: _ref, ...restPropsWithoutSx } = restProps;
  const [variantProps, localProps] = typography.splitVariantProps(restPropsWithoutSx);

  const {
    as: AsComponent = 'p',
    className: classNameProp,
    ...others
  } = localProps as unknown as Omit<TypographyProps<TAs>, keyof TypographyVariant | 'ref'>;

  const Component = AsComponent as ElementType;

  const className = css(styleProps, sx);

  return (
    <Component ref={ref} className={cx(typography(variantProps), className, classNameProp)} {...others} />
  );
}
/**
 * 타입을 **명시적으로** 붙인다. `fixedForwardRef(TextBase)` 를 그대로 내보내면 선언 방출기가
 * 제네릭 `P` 를 추론하지 못해 `.d.ts` 에 `(props: any)` 로 찍히고, **배포 패키지를 쓰는 쪽에서만**
 * 자동완성과 타입 검사가 통째로 사라진다(소스로 쓰는 모노레포 안에서는 안 보이는 결함).
 */
export const Text: <TAs extends ElementType = 'p'>(
  props: TypographyProps<TAs>,
) => ReactNode = fixedForwardRef(TextBase);

export default Text;
